import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, UserCircle, LogIn } from 'lucide-react';
import { Button } from './ui/button';
import MegaMenu from './MegaMenu';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';

const MENU_ITEMS = [
  { key: 'services', label: 'Our Services' },
  { key: 'pricing', label: 'Pricing' },
  { key: 'gallery', label: 'Gallery' },
  { key: 'locations', label: 'Service Locations' },
  { key: 'testimonials', label: 'Testimonials' },
  { key: 'blog', label: 'Blog & News', to: '/blog' },
];

const CLOSE_DELAY_MS = 220;

const DesktopNav = ({ onOpenAuth, hideAccountCta = false }) => {
  const [activeMenu, setActiveMenu] = useState(null);
  const [lockedOpen, setLockedOpen] = useState(false); // true after click → only outside-click / ESC closes
  const closeTimerRef = useRef(null);
  const containerRef = useRef(null);
  const { isLoggedIn, customer } = useCustomerAuth();

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const closeMenu = useCallback(() => {
    cancelClose();
    setActiveMenu(null);
    setLockedOpen(false);
  }, [cancelClose]);

  const scheduleClose = useCallback(() => {
    if (lockedOpen) return; // click-locked menus only close via outside click / ESC
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      setActiveMenu(null);
      closeTimerRef.current = null;
    }, CLOSE_DELAY_MS);
  }, [cancelClose, lockedOpen]);

  const openMenu = useCallback((key) => {
    cancelClose();
    setActiveMenu(key);
  }, [cancelClose]);

  const toggleMenuClick = useCallback((key) => {
    cancelClose();
    if (activeMenu === key && lockedOpen) {
      // Same menu clicked while locked-open → close
      setActiveMenu(null);
      setLockedOpen(false);
    } else {
      setActiveMenu(key);
      setLockedOpen(true);
    }
  }, [activeMenu, lockedOpen, cancelClose]);

  // Outside click closes menu
  useEffect(() => {
    if (!activeMenu) return undefined;
    const handlePointerDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [activeMenu, closeMenu]);

  // ESC closes menu (capture phase to beat any descendant key handlers)
  useEffect(() => {
    if (!activeMenu) return undefined;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('keydown', handleKey, true);
    return () => document.removeEventListener('keydown', handleKey, true);
  }, [activeMenu, closeMenu]);

  // Cleanup timer on unmount
  useEffect(() => () => cancelClose(), [cancelClose]);

  return (
    <div ref={containerRef} className="contents">
      {/* Trigger row */}
      <div className="hidden lg:flex items-center gap-8">
        <Link
          to="/"
          className="text-green-600 font-medium hover:text-green-700 transition-colors py-2"
          data-testid="nav-home"
        >
          Home
        </Link>

        {MENU_ITEMS.map((item) => {
          // Plain link items (no mega menu)
          if (item.to) {
            return (
              <Link
                key={item.key}
                to={item.to}
                onClick={closeMenu}
                className="py-2 font-medium text-gray-700 hover:text-green-600 transition-colors"
                data-testid={`nav-link-${item.key}`}
              >
                {item.label}
              </Link>
            );
          }
          const isActive = activeMenu === item.key;
          return (
            <div
              key={item.key}
              className="relative"
              onMouseEnter={() => openMenu(item.key)}
              onMouseLeave={scheduleClose}
            >
              {/* Padding-y on the button creates a hover-stable hit area;
                  the dropdown is positioned at top-full of the <nav>, so no visual gap. */}
              <button
                type="button"
                onClick={() => toggleMenuClick(item.key)}
                aria-haspopup="true"
                aria-expanded={isActive}
                data-testid={`nav-trigger-${item.key}`}
                className={`flex items-center gap-1 py-2 font-medium transition-colors ${
                  isActive
                    ? 'text-green-600'
                    : 'text-gray-700 hover:text-green-600'
                }`}
              >
                {item.label}
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-200 ${isActive ? 'rotate-180' : ''}`}
                />
              </button>
            </div>
          );
        })}

        {/* Contact Us moved to the hero section CTAs */}
        {/* Account / Login CTA — hidden on pages that render the profile in the top brand strip */}
        {!hideAccountCta && (
          isLoggedIn ? (
            <Link to="/account">
              <Button variant="outline" className="border-green-600 text-green-700 hover:bg-green-50" data-testid="nav-account-btn">
                <UserCircle className="mr-2" size={16} />
                <span className="hidden lg:inline">{(customer?.name || 'Account').split(' ')[0]}</span>
                <span className="lg:hidden">Me</span>
              </Button>
            </Link>
          ) : (
            <Button
              variant="outline"
              onClick={() => onOpenAuth?.('login')}
              className="border-green-600 text-green-700 hover:bg-green-50"
              data-testid="nav-login-btn"
            >
              <LogIn className="mr-2" size={16} />
              Log in
            </Button>
          )
        )}
      </div>

      {/* Mega menu drawer — full width, directly under nav, no visual gap.
          Hover anywhere over it cancels close timer, so cursor traversal is stable. */}
      {activeMenu && (
        <div
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          data-testid="mega-menu-wrapper"
        >
          <MegaMenu type={activeMenu} isOpen onItemClick={closeMenu} />
        </div>
      )}
    </div>
  );
};

export default DesktopNav;
